/**
 * Utilitários compartilhados para operações CRUD
 * Reduz duplicação de código entre módulos
 */
import { limparMascarasDeCamposComuns } from './formatadores';

/**
 * Garante que st_codHashEmpresa está presente nos dados antes de salvar
 * @param {Object} formData - Dados do formulário
 * @param {string} codHashEmpresa - Código hash da empresa (opcional, será obtido do localStorage se não fornecido)
 * @returns {Object} Dados com st_codHashEmpresa garantido
 */
export const garantirCodHashEmpresa = (formData, codHashEmpresa = null) => {
  const codHash = codHashEmpresa || localStorage.getItem("st_codHashEmpresa");
  return {
    ...formData,
    st_codHashEmpresa: codHash || formData.st_codHashEmpresa
  };
};

/**
 * Limpa campos vazios do objeto, convertendo para null
 * Preserva campos importantes como st_codHashEmpresa
 * @param {Object} dados - Dados a serem limpos
 * @param {Array<string>} camposPreservar - Campos que não devem ser convertidos para null
 * @returns {Object} Dados limpos
 */
export const limparCamposVazios = (dados, camposPreservar = ['st_codHashEmpresa']) => {
  const dadosLimpos = { ...dados };
  
  Object.keys(dadosLimpos).forEach(key => {
    if (!camposPreservar.includes(key) && (dadosLimpos[key] === "" || dadosLimpos[key] === undefined)) {
      dadosLimpos[key] = null;
    }
  });
  
  return dadosLimpos;
};

/**
 * Prepara dados para salvamento, garantindo st_codHashEmpresa e limpando campos vazios
 * @param {Object} formData - Dados do formulário
 * @param {Array<string>} camposPreservar - Campos que não devem ser convertidos para null
 * @param {Array<string>} camposComMascara - Campos adicionais (além dos padrão) que devem ter suas máscaras removidas.
 * @returns {Object} Dados preparados para envio
 */
export const prepararDadosParaSalvar = (formData, camposPreservar = ['st_codHashEmpresa'], camposComMascara = []) => {
  const codHashEmpresa = localStorage.getItem("st_codHashEmpresa");
  
  // Garantir st_codHashEmpresa
  let dadosFormatados = garantirCodHashEmpresa(formData, codHashEmpresa);
  
  // Limpar máscaras de campos comuns (CPF, CNPJ, Telefone, CEP, etc)
  dadosFormatados = limparMascarasDeCamposComuns(dadosFormatados, camposComMascara);

  // Garantir que st_codHashEmpresa não seja null
  if (!dadosFormatados.st_codHashEmpresa && codHashEmpresa) {
    dadosFormatados.st_codHashEmpresa = codHashEmpresa;
  }
  
  // Limpar campos vazios
  dadosFormatados = limparCamposVazios(dadosFormatados, camposPreservar);
  
  return dadosFormatados;
};

/**
 * Trata erros de API de forma padronizada
 * @param {Error} error - Erro capturado
 * @param {Function} setError - Função para definir mensagem de erro
 * @param {string} operacao - Nome da operação (ex: 'Salvar categoria')
 */
export const tratarErroApi = (error, setError, operacao = 'Operação') => {
  if (error.response?.status === 401) {
    setError("Sessão expirada. Por favor, faça login novamente.");
    localStorage.removeItem("token");
    localStorage.removeItem("usuario");
    window.location.href = "/login";
  } else if (error.response?.status === 403) {
    setError("Acesso negado. Você não tem permissão para realizar esta ação.");
  } else if (error.response?.status === 404) {
    setError("Recurso não encontrado.");
  } else if (error.response?.status === 500) {
    setError(`Erro do servidor ao ${operacao.toLowerCase()}. Tente novamente mais tarde.`);
  } else {
    const mensagemErro = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        `Erro ao ${operacao.toLowerCase()}`;
    setError(mensagemErro);
  }
};

/**
 * Obtém o código hash da empresa do usuário logado
 * @returns {string|null} Código hash da empresa ou null
 */
export const obterCodHashEmpresa = () => {
  return localStorage.getItem("st_codHashEmpresa");
};

/**
 * Obtém o código hash da empresa do usuário logado ou do objeto fornecido
 * @param {Object} objeto - Objeto que pode conter st_codHashEmpresa
 * @returns {string|null} Código hash da empresa ou null
 */
export const obterCodHashEmpresaOuPadrao = (objeto = null) => {
  if (objeto?.st_codHashEmpresa) {
    return objeto.st_codHashEmpresa;
  }
  return obterCodHashEmpresa();
};
